import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, AUTOCOMPLETE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const ChangeMultipleModuleModal = NiceModal.create(({ selectedRoles }) => {
	const dispatch = useDispatch();
	const { showError, showSuccess } = useAlert();
	const axiosPrivate = useAxiosPrivate();
	const modal = useModal();

	const handleCloseModal = () => {
		modal.remove();
	};

	const formMethods = useForm();

	const {
		setValue,
		watch,
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		for await (const item of selectedRoles) {
			await axiosPrivate
				.post(ADMIN.ROLE.GET_ROLE_BY_ID, {
					ID: item.ID
				})
				.then((response) => {
					const roleObject = response.data;
					if (roleObject.moduleIDs?.includes(data.oldModule)) {
						const filteredRoles = roleObject.moduleIDs.filter((module) => module !== data.oldModule);
						const newModuleIDs = [...filteredRoles, data.newModule];

						axiosPrivate
							.post(ADMIN.ROLE.UPDATE_ROLES, {
								IDs: [item.ID],
								moduleIDs: newModuleIDs?.join(',') ?? null
							})
							.catch((error) => {
								AppLogger.error(error);
								showError();
							});
					}
				});
		}

		dispatch(updateDynamicListData(DYNAMICLISTS.ACCESS_CONTROL));

		showSuccess();
		handleCloseModal();
	};

	const userAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'name', width: 1000 }
		],
		[]
	);

	return (
		<Modal maxWidth="small" fullwidth className="modal-changeMultipleModule">
			<ModalTitle>
				<Typography variant="p1">changeModule</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="ids" required>
						<Typography>{selectedRoles.map((item) => item.ID).join(', ')}</Typography>
					</FormItem>

					<FormItem label="oldModule" required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ROLES}
							value={watch('oldModule')}
							setValue={(value) => {
								setValue('oldModule', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="oldModule"
							columns={userAutocompleteColumns}
							selectionLabelAccessor={['name']}
						/>
					</FormItem>

					<FormItem label="newModule" required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ROLES}
							value={watch('newModule')}
							setValue={(value) => {
								setValue('newModule', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="newModule"
							columns={userAutocompleteColumns}
							selectionLabelAccessor={['name']}
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button variant="outline" label="cancel" onClick={handleCloseModal} />
				<Button
					label="update"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default ChangeMultipleModuleModal;
